import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { io } from 'socket.io-client'

import {
	KeyboardArrowDown as KeyboardArrowDownIcon,
	Search as SearchIcon,
} from '@mui/icons-material'

import {
	Button,
	TextField,
} from '@mui/material'

import DefaultModal from '../components/modal/DefaultModal/DefaultModal'

import dayjs from 'dayjs'

import { useNavigate, useSearchParams } from 'react-router-dom'
import { emitSnackbar } from "../lib/snackbar-emitter"

import {
	formatDateLabel,
	formatPhoneNumber,
	formatTimestampLabel,
	formatKwikMessageContent,
	getDefaultScoutDatePreset,
	getDefaultScoutDatePresetValue,
	getScoutConversationExport,
	getPreferredAccount,
	getScoutMessageById,
	exportScoutConversationMessages,
	getScoutAccountAnalytics,
	getScoutConversationAnalytics,
	getScoutContext,
	isScoutAccountActive,
	listScoutDateOptions,
	listScoutAccounts,
	listScoutConversations,
	listScoutMessages,
	normalizeScoutDate,
	getScoutEnterprise,
	searchScoutMessages,
} from '../services/scout'
import { getSearchMessageSender, normalizeQueryText } from '../components/search/SearchHighlight'
import SearchResultsModal from '../components/search/SearchResultsModal'
import { useSnackbar } from '../components/SnackbarContext'
import { getVisibleConversations } from './conversation-normalizer'
import ConversationSidebar from '../components/conversations/ConversationSidebar'
import ConversationOverview from '../components/conversations/ConversationOverview'
import ConversationInsightsSidebar from '../components/conversations/ConversationInsightsSidebar'
import ConversationProfileSidebar from '../components/conversations/ConversationProfileSidebar'
import ConversationThread from '../components/conversations/ConversationThread'
import {
	getHistoryEntryKey,
	getMessageIdentifier,
	getMessageKey,
	LinkifiedText,
} from '../components/conversations/ConversationMessageBody'

import { createHomeGraphicsViewModel } from '../components/home/graphics'

import HomeGraphicsSection from '../components/home/graphics'

import {
	getContactData,
	createContact,
	updateContact,
} from "../services/scout"

import {
	renderCRMFeatures,
	renderExportConversation,
} from "../services/utils"

import { getGreetings } from "../utils"

const DEFAULT_MESSAGE_LIMIT = 20
const TOP_LOAD_THRESHOLD = 72

const SOCKET_EVENTS = [
	'messages.upsert',
	// 'chats.upsert',
	// 'chats.update',
]
const SOCKET_TRANSPORTS = ['websocket']
const SCOUT_WEBSOCKET_ENABLED = process.env.REACT_APP_SCOUT_USE_WEBSOCKET === 'true'
const MEDIA_MESSAGE_TYPES = new Set([
	'imageMessage',
	'videoMessage',
	'documentMessage',
	'documentWithCaptionMessage',
	'audioMessage',
	'stickerMessage',
])

const normalizeSocketUrl = (value) => {
	if (!value || typeof value !== 'string') {
		return null
	}

	return value.trim().replace(/\/+$/, '') || null
}

const normalizeSocketPath = (value) => {
	if (!value || typeof value !== 'string') {
		return '/socket.io'
	}

	const normalized = value.trim().replace(/\/+$/, '')
	if (!normalized) {
		return '/socket.io'
	}

	return normalized.startsWith('/') ? normalized : `/${normalized}`
}

const isInsecureSocketUrlForPage = (value) => {
	if (!value || window.location.protocol !== 'https:') {
		return false
	}

	try {
		const parsed = new URL(value)
		return parsed.protocol === 'http:'
	} catch {
		return false
	}
}

const getSocketConnectionConfig = () => {
	const candidates = [
		process.env.REACT_APP_WEBSOCKET_URL,
		process.env.REACT_APP_DEFAULT_EVO_API_URL,
		process.env.REACT_APP_HOST,
		window.location.origin,
	]
	const skippedInsecureUrls = []
	const seen = new Set()

	const urls = candidates
		.map(normalizeSocketUrl)
		.filter((value) => {
			if (!value || seen.has(value)) {
				return false
			}

			seen.add(value)

			if (isInsecureSocketUrlForPage(value)) {
				skippedInsecureUrls.push(value)
				return false
			}

			return true
		})

	return {
		urls,
		path: normalizeSocketPath(process.env.REACT_APP_WEBSOCKET_PATH),
		skippedInsecureUrls,
	}
}

const isRenderableMessage = (message) => {
	const messageType = String(message?.messageType || message?.messagetype || '')
	return !message?.type && messageType !== 'reactionMessage' && messageType !== 'secretEncryptedMessage'
}

const getEditHistory = (message, messages = []) => {
	if (!message?.id || !Array.isArray(messages)) {
		return []
	}

	const messageId = getMessageIdentifier(message)
	const seenHistoryEntries = new Set()

	return messages
		.filter((entry) => {
			const protocolId = entry?.protocolId || entry?.protocol_id
			return Boolean(entry?.type && protocolId && String(protocolId) === messageId)
		})
		.filter((entry) => {
			const historyKey = getHistoryEntryKey(entry)
			if (seenHistoryEntries.has(historyKey)) {
				return false
			}

			seenHistoryEntries.add(historyKey)
			return true
		})
		.sort((first, second) => Number(first?.timestamp || 0) - Number(second?.timestamp || 0))
}

const getReactionTargetId = (message) => {
	const reactionPayload = message?.message
	if (!reactionPayload || typeof reactionPayload !== 'object') {
		return ''
	}

	return String(reactionPayload.reactedMessageId || reactionPayload.messageId || '')
}

const getReactionEmoji = (message) => {
	const reactionPayload = message?.message
	if (!reactionPayload || typeof reactionPayload !== 'object') {
		return ''
	}

	if (typeof reactionPayload.reaction === 'string') {
		return reactionPayload.reaction
	}

	return ''
}

const getReactionActorKey = (message) => {
	const reactionPayload = message?.message
	if (!reactionPayload || typeof reactionPayload !== 'object') {
		return getMessageIdentifier(message)
	}

	return String(reactionPayload.contact || reactionPayload.fromMe || getMessageIdentifier(message))
}

const buildMessageReactionLookup = (messages = []) => {
	const reactionsByMessageId = new Map()

	messages
		.filter((entry) => entry?.messageType === 'reactionMessage')
		.sort((first, second) => Number(first?.timestamp || 0) - Number(second?.timestamp || 0))
		.forEach((reactionMessage) => {
			const targetId = getReactionTargetId(reactionMessage)
			if (!targetId) {
				return
			}

			const actorKey = getReactionActorKey(reactionMessage)
			const emoji = getReactionEmoji(reactionMessage)
			const currentReactions = reactionsByMessageId.get(targetId) || new Map()

			if (emoji) {
				currentReactions.set(actorKey, {
					id: `${targetId}-${actorKey}`,
					emoji,
					fromMe: Boolean(reactionMessage?.message?.fromMe),
					timestamp: Number(reactionMessage?.timestamp || 0),
				})
			} else {
				currentReactions.delete(actorKey)
			}

			if (currentReactions.size) {
				reactionsByMessageId.set(targetId, currentReactions)
			} else {
				reactionsByMessageId.delete(targetId)
			}
		})

	return reactionsByMessageId
}

const validateEmail = (value = '') => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim())

const toDateTimeLocal = (value) => {
	return dayjs(value).format('YYYY-MM-DDTHH:mm')
}

const getConversationTitle = (conversation) => {
	if (!conversation) {
		return 'Conversa'
	}

	return conversation.kwik_contact_name || conversation.name || formatPhoneNumber(conversation.phone_num)
}

const isAbortError = (error) => error?.name === 'AbortError'

const mergeMessages = (olderMessages, currentMessages) => {
	const mergedMap = new Map()

	;[...olderMessages, ...currentMessages].forEach((message, index) => {
		mergedMap.set(getMessageKey(message, index), message)
	})

	return Array.from(mergedMap.values()).sort((first, second) => {
		const firstValue = Number(first?.timestamp || 0)
		const secondValue = Number(second?.timestamp || 0)

		return firstValue - secondValue
	})
}

const shouldIgnoreThreadMessage = (message) => {
	return String(message?.messageType || message?.messagetype || '') === 'pollUpdateMessage'
}

const filterThreadMessages = (messageList = []) => {
	if (!Array.isArray(messageList)) {
		return []
	}

	return messageList.filter((message) => !shouldIgnoreThreadMessage(message))
}

const normalizeTimestamp = (value) => {
	if (typeof value === 'number') {
		return Number.isFinite(value) ? value : 0
	}

	if (typeof value === 'string') {
		const numericValue = Number(value)
		return Number.isFinite(numericValue) ? numericValue : 0
	}

	if (value && typeof value === 'object') {
		const low = Number(value.low || 0)
		const high = Number(value.high || 0)

		if (Number.isFinite(low) && Number.isFinite(high)) {
			return high * (2 ** 32) + low
		}
	}

	return 0
}

const parseConversationIdentity = (value) => {
	const remoteJid = String(value || '').trim()
	if (!remoteJid) {
		return {
			remoteJid: '',
			phoneNumber: '',
			type: 'CONTACT',
		}
	}

	const [phoneNumber = '', suffix = ''] = remoteJid.split('@')

	return {
		remoteJid,
		phoneNumber,
		type: suffix === 'g.us' ? 'GROUP' : 'CONTACT',
	}
}

const getSocketDataItems = (payload) => {
	if (Array.isArray(payload?.data)) {
		return payload.data
	}

	if (payload?.data) {
		return [payload.data]
	}

	return []
}

const getRealtimeMessageRemoteJid = (message) => {
	return String(
		message?.key?.remoteJid
			|| message?.remoteJid
			|| message?.source
			|| message?.message?.reactionMessage?.key?.remoteJid
			|| '',
	).trim()
}

const buildRealtimeReactionPayload = (message) => {
	const reactionPayload = message?.message?.reactionMessage || {}
	const fromMe = Boolean(message?.key?.fromMe)
	const reactedMessageId = reactionPayload?.key?.id || ''
	const conversation = reactionPayload?.key?.remoteJid || message?.key?.remoteJid || ''
	const contact = fromMe
		? conversation
		: message?.key?.participant || message?.key?.remoteJid || ''

	return {
		reaction: reactionPayload?.text || '',
		fromMe,
		contact,
		reactedMessageId,
		conversation,
	}
}

const getRealtimeMessageType = (message) => message?.messageType || message?.messagetype || ''

const getPollUpdateCreationMessageId = (message) => {
	return String(
		message?.message?.pollUpdateMessage?.pollCreationMessageKey?.id
		|| message?.pollCreationMessageKey?.id
			|| '',
	).trim()
}

const getRealtimePollUpdates = (message) => {
	if (Array.isArray(message?.pollUpdates)) {
		return message.pollUpdates
	}

	if (Array.isArray(message?.message?.pollUpdates)) {
		return message.message.pollUpdates
	}

	return null
}

const isMessageMatchingPollCreation = (message, pollCreationMessageId) => {
	if (!pollCreationMessageId) {
		return false
	}

	return [
		getMessageIdentifier(message),
		message?.id,
		message?.chatMessageId,
		message?.messageId,
		message?.key?.id,
		message?.message?.key?.id,
	].some((candidate) => String(candidate || '') === pollCreationMessageId)
}

const applyRealtimePollUpdateToMessages = (currentMessages = [], rawMessage) => {
	const pollCreationMessageId = getPollUpdateCreationMessageId(rawMessage)
	const pollUpdates = getRealtimePollUpdates(rawMessage)

	if (!pollCreationMessageId || !Array.isArray(pollUpdates)) {
		return currentMessages
	}

	return currentMessages.map((message) => isMessageMatchingPollCreation(message, pollCreationMessageId)
		? { ...message, pollUpdates }
		: message)
}

const normalizeRealtimeMessage = (rawMessage) => {
	if (!rawMessage || typeof rawMessage !== 'object') {
		return null
	}

	const messageType = rawMessage?.messageType || rawMessage?.messagetype || ''
	const payload = rawMessage?.message && typeof rawMessage.message === 'object' ? rawMessage.message : {}
	const baseMessage = {
		timestamp: normalizeTimestamp(rawMessage?.messageTimestamp || rawMessage?.timestamp),
		fromMe: Boolean(rawMessage?.key?.fromMe ?? rawMessage?.fromMe),
		messageType,
		source: getRealtimeMessageRemoteJid(rawMessage),
		pushName: rawMessage?.pushName || '',
		id: rawMessage?.key?.id || rawMessage?.id || rawMessage?.chatMessageId || '',
		restricted: rawMessage?.restricted,
		transcription: rawMessage?.transcription,
		transcription_error: rawMessage?.transcription_error,
		transcription_status: rawMessage?.transcription_status,
		status: rawMessage?.status,
	}

	if (!messageType || messageType === 'secretEncryptedMessage') {
		return null
	}

	if (messageType === 'conversation') {
		return {
			...baseMessage,
			message: payload?.conversation || '',
			simplified_type: 'text',
		}
	}

	if (messageType === 'reactionMessage') {
		return {
			...baseMessage,
			message: buildRealtimeReactionPayload(rawMessage),
		}
	}

	if (['protocolMessage', 'REVOKE', 'editedMessage'].includes(messageType)) {
		const protocolMessage = payload?.protocolMessage || payload?.editedMessage?.message?.protocolMessage || {}
		const editedText = payload?.conversation || payload?.extendedTextMessage?.text || ''

		return {
			...baseMessage,
			type: editedText ? 'EDITED' : 'DELETED',
			message: editedText,
			protocolId: protocolMessage?.key?.id || protocolMessage?.key?.oldid || '',
			messageType: 'protocolMessage',
		}
	}

	if (MEDIA_MESSAGE_TYPES.has(messageType) || messageType === 'ptvMessage') {
		const resolvedType = messageType === 'ptvMessage' ? 'videoMessage' : messageType
		const mediaPayload = payload?.[resolvedType] || payload?.[messageType] || payload || {}
		const fileLength = typeof mediaPayload?.fileLength === 'object'
			? normalizeTimestamp(mediaPayload.fileLength)
			: Number(mediaPayload?.fileLength || mediaPayload?.fileSize || 0)

		return {
			...baseMessage,
			messageType: resolvedType,
			message: {
				...mediaPayload,
				base64: mediaPayload?.base64 || payload?.base64 || '',
				mediaUrl: mediaPayload?.mediaUrl || payload?.mediaUrl || '',
			},
			caption: mediaPayload?.caption || '',
			title: mediaPayload?.title || mediaPayload?.fileName || '',
			fileName: mediaPayload?.fileName || '',
			mimetype: mediaPayload?.mimetype || '',
			size: fileLength,
			thumbnail: mediaPayload?.jpegThumbnail || '',
			gifPlayback: Boolean(mediaPayload?.gifPlayback),
		}
	}

	if (messageType === 'contactMessage') {
		const contactPayload = payload?.contactMessage || payload || {}
		return {
			...baseMessage,
			...contactPayload,
			contactMessage: contactPayload,
			message: contactPayload,
			simplified_type: 'contact',
		}
	}

	if (messageType === 'locationMessage' || messageType === 'liveLocationMessage') {
		const locationPayload = payload?.[messageType] || payload || {}
		return {
			...baseMessage,
			...locationPayload,
			[messageType]: locationPayload,
			message: locationPayload,
			thumbnail: locationPayload?.jpegThumbnail || '',
			simplified_type: 'location',
		}
	}

	if (messageType === 'eventMessage') {
		const eventPayload = payload?.eventMessage || payload || {}
		return {
			...baseMessage,
			...eventPayload,
			eventMessage: eventPayload,
			message: eventPayload,
			simplified_type: 'event',
		}
	}

	if (messageType === 'pollCreationMessageV3') {
		const pollPayload = payload?.pollCreationMessageV3 || payload || {}
		return {
			...baseMessage,
			...pollPayload,
			pollCreationMessageV3: pollPayload,
			message: pollPayload,
			pollUpdates: Array.isArray(rawMessage?.pollUpdates) ? rawMessage.pollUpdates : undefined,
			simplified_type: 'poll_creaction_message_v3',
		}
	}

	return {
		...baseMessage,
		message: payload,
	}
}

const sortConversationsByLastMessage = (conversationList = []) => {
	return [...conversationList].sort((first, second) => {
		return normalizeTimestamp(second?.last_message_timestamp) - normalizeTimestamp(first?.last_message_timestamp)
	})
}

const upsertConversation = (conversationList, incomingConversation) => {
	const conversationId = String(incomingConversation?.id || '').trim()
	if (!conversationId) {
		return conversationList
	}

	const conversationMap = new Map(
		conversationList.map((conversation) => [String(conversation?.id || ''), conversation]),
	)
	const existingConversation = conversationMap.get(conversationId) || null
	const mergedConversation = {
		...existingConversation,
		...incomingConversation,
		id: conversationId,
		_id: incomingConversation?._id || existingConversation?._id || conversationId,
		avatarUrl:
			incomingConversation?.avatarUrl
			|| incomingConversation?.profile_picture
			|| existingConversation?.avatarUrl
			|| existingConversation?.profile_picture
			|| '',
	}

	conversationMap.set(conversationId, mergedConversation)

	return sortConversationsByLastMessage(Array.from(conversationMap.values()))
}

const buildConversationFromRealtimeMessage = ({ message, existingConversation, selectedConversationId }) => {
	const remoteJid = getRealtimeMessageRemoteJid(message)
	if (!remoteJid) {
		return null
	}

	const normalizedMessage = normalizeRealtimeMessage(message)
	if (!normalizedMessage) {
		return null
	}

	const isDeletedRealtimeMessage = normalizedMessage.type === 'DELETED'
	const conversationPreviewMessage = isDeletedRealtimeMessage
		? {
			type: 'DELETED',
			message: 'Mensagem apagada',
			protocolId: normalizedMessage.protocolId || '',
		}
		: normalizedMessage.message

	const identity = parseConversationIdentity(remoteJid)
	const isSelectedConversation = String(selectedConversationId || '') === remoteJid
	const currentUnread = Number(
		existingConversation?.unreadMessages
			?? existingConversation?.unread_messages
			?? existingConversation?.unreadCount
			?? 0,
	)
	const nextUnread = normalizedMessage.fromMe || isSelectedConversation
		? 0
		: currentUnread + 1

	return {
		...existingConversation,
		id: remoteJid,
		_id: remoteJid,
		name: existingConversation?.name || (!normalizedMessage.fromMe ? normalizedMessage.pushName : '') || identity.phoneNumber,
		owner: message?.instanceId || existingConversation?.owner,
		type: existingConversation?.type || identity.type,
		phone_num: existingConversation?.phone_num || identity.phoneNumber,
		message: conversationPreviewMessage,
		message_type: normalizedMessage.messageType,
		last_message_timestamp: normalizedMessage.timestamp || existingConversation?.last_message_timestamp || 0,
		fromMe: normalizedMessage.fromMe,
		profile_picture: existingConversation?.profile_picture || '',
		sender: normalizedMessage.pushName || existingConversation?.sender || '',
		kwik_contact_id: existingConversation?.kwik_contact_id,
		kwik_contact_name: existingConversation?.kwik_contact_name,
		instancename: existingConversation?.instancename || message?.instance,
		unreadMessages: nextUnread,
	}
}

const buildConversationFromRealtimeChat = ({ chat, existingConversation }) => {
	const remoteJid = String(chat?.remoteJid || chat?.id || '').trim()
	if (!remoteJid) {
		return null
	}

	const identity = parseConversationIdentity(remoteJid)

	return {
		...existingConversation,
		id: remoteJid,
		_id: remoteJid,
		name: chat?.name || existingConversation?.name || identity.phoneNumber,
		owner: chat?.instanceId || existingConversation?.owner,
		type: existingConversation?.type || identity.type,
		phone_num: existingConversation?.phone_num || identity.phoneNumber,
		profile_picture: existingConversation?.profile_picture || '',
		kwik_contact_id: existingConversation?.kwik_contact_id,
		kwik_contact_name: existingConversation?.kwik_contact_name,
		instancename: existingConversation?.instancename,
		unreadMessages: Number(chat?.unreadMessages ?? existingConversation?.unreadMessages ?? 0),
		last_message_timestamp: existingConversation?.last_message_timestamp || 0,
	}
}

const Conversations = () => {
	const navigate = useNavigate()
	const [searchParams, setSearchParams] = useSearchParams()
	const initialDateOptions = listScoutDateOptions()
	const initialSelectedPreset = searchParams.get('period') || getDefaultScoutDatePreset()
	const initialSelectedOption = initialDateOptions.find((option) => option.key === initialSelectedPreset) || initialDateOptions[0]
	const { user } = getScoutContext()
	const { showSnackbar } = useSnackbar()
	const threadRef = useRef(null)
	const messagesLoadingRef = useRef(false)
	const conversationViewVersionRef = useRef(0)
	const activeThreadRequestRef = useRef({ controller: null, version: 0 })
	const selectedConversationIdRef = useRef(searchParams.get('conversationId') || '')
	const threadNearBottomRef = useRef(true)
	const pendingHistoryScrollAdjustmentRef = useRef(null)
	const suppressThreadScrollHandlerRef = useRef(false)
	const dateDropdownRef = useRef(null)
	const [accounts, setAccounts] = useState([])
	const [selectedAccountId, setSelectedAccountId] = useState(searchParams.get('accountId') || '')
	const [selectedConversationId, setSelectedConversationId] = useState(searchParams.get('conversationId') || '')
	const [dateValue, setDateValue] = useState(() => normalizeScoutDate(searchParams.get('date') || initialSelectedOption?.endDate || getDefaultScoutDatePresetValue()))
	const [selectedDatePreset, setSelectedDatePreset] = useState(initialSelectedPreset)
	const [isDateMenuOpen, setIsDateMenuOpen] = useState(false)
	const [anchorMessageId, setAnchorMessageId] = useState(searchParams.get('anchorMessageId') || '')
	const [searchValue, setSearchValue] = useState('')
	const [searchResultsOpen, setSearchResultsOpen] = useState(false)
	const [searchResultsLoading, setSearchResultsLoading] = useState(false)
	const [searchResults, setSearchResults] = useState([])
	const [searchFeedback, setSearchFeedback] = useState('')
	const searchDebounceTimerRef = useRef(null)
	const [analytics, setAnalytics] = useState(null)
	const [conversationAnalytics, setConversationAnalytics] = useState(null)
	const [conversations, setConversations] = useState([])
	const [messages, setMessages] = useState([])
	const [messageCursor, setMessageCursor] = useState(0)
	const [initialMessagesLoading, setInitialMessagesLoading] = useState(false)
	const [messagesLoading, setMessagesLoading] = useState(false)
	const [loadingMediaIds, setLoadingMediaIds] = useState({})
	const [exportDialogOpen, setExportDialogOpen] = useState(false)
	const [exportEmail, setExportEmail] = useState('')
	const [exportType, setExportType] = useState('pdf')
	const [exportDateStart, setExportDateStart] = useState(toDateTimeLocal(dayjs().subtract(7, 'day').hour(8).minute(0)))
	const [exportDateEnd, setExportDateEnd] = useState(toDateTimeLocal(dayjs().hour(18).minute(0)))
	const [exportLoading, setExportLoading] = useState(false)
	const [exportFeedback, setExportFeedback] = useState('')
	const [rightSidebar, setRightSidebar] = useState(window.innerWidth >= 600 ? "insights" : null)
	const [mobileChartOpen, setMobileChartOpen] = useState(null)
	const [loading, setLoading] = useState(true)
	const [threadNearBottom, setThreadNearBottom] = useState(true)
	const [pendingNewMessagesCount, setPendingNewMessagesCount] = useState(0)
	const socketRef = useRef(null)

	const [enterprise, setEnterprise] = useState(null)
	const [optionsAnchorEl, setOptionsAnchorEl] = useState(null)
	const [contact, setContact] = useState(null)

	const frozenNamesRef = useRef(new Map())

	const getStableConversationTitle = useCallback((conversation) => {
		if (!conversation?.id) return 'Conversa'

		const existing = frozenNamesRef.current.get(conversation.id)
		if (existing) return existing

		const resolved = getConversationTitle(conversation)
		if (resolved && resolved.trim()) {
			frozenNamesRef.current.set(conversation.id, resolved)
		}

		return resolved || 'Conversa'
	}, [])

	const baseDateOptions = useMemo(() => listScoutDateOptions(), [])
	
	const selectedDateOption = useMemo(() => {
		return baseDateOptions.find((option) => option.key === selectedDatePreset) || baseDateOptions[0] || null
	}, [baseDateOptions, selectedDatePreset])
	
	const selectedDateRange = useMemo(() => {
		if (selectedDateOption) {
			return {
				startDate: selectedDateOption.startDate || selectedDateOption.value || dateValue,
				endDate: selectedDateOption.endDate || selectedDateOption.value || dateValue,
			}
		}

		return {
			startDate: dateValue,
			endDate: dateValue,
		}
	}, [dateValue, selectedDateOption])

	const selectedDateLabel = useMemo(() => {
		return selectedDateOption?.label || formatDateLabel(dateValue)
	}, [dateValue, selectedDateOption])

	const abortManagedRequest = useCallback((requestRef) => {
		if (requestRef.current?.controller) {
			requestRef.current.controller.abort()
			requestRef.current.controller = null
		}
	}, [])

	const resetConversationLoadingState = useCallback(() => {
		messagesLoadingRef.current = false
		pendingHistoryScrollAdjustmentRef.current = null
		suppressThreadScrollHandlerRef.current = false
		setInitialMessagesLoading(false)
		setMessagesLoading(false)
	}, [])

	const cancelConversationRequests = useCallback(() => {
		conversationViewVersionRef.current += 1
		abortManagedRequest(activeThreadRequestRef)
		resetConversationLoadingState()
	}, [abortManagedRequest, resetConversationLoadingState])

	const resetConversationViewState = useCallback(() => {
		pendingHistoryScrollAdjustmentRef.current = null
		suppressThreadScrollHandlerRef.current = false
		setMessages([])
		setConversationAnalytics(null)
		setMessageCursor(0)
		setPendingNewMessagesCount(0)
		setLoadingMediaIds({})
		setContact(null)
	}, [])

	const selectConversation = useCallback((conversationId, { accountId, anchorId = '' } = {}) => {
		if (selectedConversationId === conversationId)
			return

		cancelConversationRequests()
		resetConversationViewState()

		if (typeof accountId !== 'undefined' && accountId !== null) {
			setSelectedAccountId(String(accountId))
		}

		setSelectedConversationId(conversationId ? String(conversationId) : '')
		setAnchorMessageId(anchorId ? String(anchorId) : '')
	}, [cancelConversationRequests, resetConversationViewState, selectedConversationId])

	const optionsOpen = Boolean(optionsAnchorEl)

	useEffect(() => {
		selectedConversationIdRef.current = selectedConversationId
	}, [selectedConversationId])

	useEffect(() => {
		threadNearBottomRef.current = threadNearBottom
	}, [threadNearBottom])

	useLayoutEffect(() => {
		const pendingAdjustment = pendingHistoryScrollAdjustmentRef.current
		if (!pendingAdjustment || !threadRef.current) {
			return
		}

		if (pendingAdjustment.version !== conversationViewVersionRef.current) {
			pendingHistoryScrollAdjustmentRef.current = null
			return
		}

		const nextScrollHeight = threadRef.current.scrollHeight
		const scrollHeightDelta = nextScrollHeight - pendingAdjustment.previousScrollHeight

		suppressThreadScrollHandlerRef.current = true
		threadRef.current.scrollTop = pendingAdjustment.previousScrollTop + scrollHeightDelta
		pendingHistoryScrollAdjustmentRef.current = null

		window.requestAnimationFrame(() => {
			suppressThreadScrollHandlerRef.current = false
		})
	}, [messages])

	useEffect(() => {
		let isMounted = true

		const loadAccounts = async () => {
			setLoading(true)

			try {
				const [accountsResult, enterpriseResult] = await Promise.allSettled([
					listScoutAccounts(),
					getScoutEnterprise(),
				])

				if (!isMounted) {
					return
				}

				if (enterpriseResult.status === "fulfilled") {
					setEnterprise(enterpriseResult.value)
				}

				const nextAccounts = accountsResult.value
				setAccounts(nextAccounts)
			} catch (nextError) {

			} finally {
				if (isMounted) {
					setLoading(false)
				}
			}
		}

		loadAccounts()

		return () => {
			isMounted = false
		}
	}, [])

	useEffect(() => {
		if (selectedAccountId || !accounts.length) {
			return
		}

		const preferredAccount = getPreferredAccount(accounts)

		if (preferredAccount) {
			setSelectedAccountId(String(preferredAccount.id))
		}
	}, [accounts, selectedAccountId])

	useEffect(() => {
		if (!isDateMenuOpen) {
			return undefined
		}

		const handlePointerDown = (event) => {
			if (!dateDropdownRef.current?.contains(event.target)) {
				setIsDateMenuOpen(false)
			}
		}

		const handleEscape = (event) => {
			if (event.key === 'Escape') {
				setIsDateMenuOpen(false)
			}
		}

		document.addEventListener('mousedown', handlePointerDown)
		document.addEventListener('keydown', handleEscape)

		return () => {
			document.removeEventListener('mousedown', handlePointerDown)
			document.removeEventListener('keydown', handleEscape)
		}
	}, [isDateMenuOpen])

	const executeSearch = async (query) => {
		const trimmed = String(query || '').trim()
		if (trimmed.length < 3) {
			setSearchResultsOpen(false)
			setSearchResults([])
			setSearchFeedback('')
			return
		}

		setSearchResultsOpen(true)
		setSearchResultsLoading(true)
		setSearchFeedback('')

		try {
			const normalizedSearch = normalizeQueryText(trimmed)
			const response = await searchScoutMessages({ text: normalizedSearch })
			const rawResults = Array.isArray(response?.data)
				? response.data
				: Array.isArray(response)
				? response
				: Object.values(response || {})

			const searchResultsNormalized = rawResults.filter((entry) => {
				const conversationName = normalizeQueryText(entry?.conversationName || '')
				const senderName = normalizeQueryText(getSearchMessageSender(entry?.messages?.[0]))
				const messageMatched = (entry?.messages || []).some((message) => {
					const textContent = normalizeQueryText(formatKwikMessageContent(message))
					const messageSender = normalizeQueryText(getSearchMessageSender(message))
					return textContent.includes(normalizedSearch) || messageSender.includes(normalizedSearch)
				})

				return conversationName.includes(normalizedSearch) || senderName.includes(normalizedSearch) || messageMatched
			})

			setSearchResults(searchResultsNormalized)
			setSearchFeedback(searchResultsNormalized.length ? '' : 'Nenhum resultado encontrado para a busca.')
		} catch (error) {
			setSearchResults([])
			setSearchFeedback('Não foi possível buscar mensagens, tente novamente.')
		} finally {
			setSearchResultsLoading(false)
		}
	}

	const handleCloseSearchResults = () => {
		setSearchResultsOpen(false)
		setSearchResults([])
		setSearchFeedback('')
	}

	useEffect(() => {
		if (searchDebounceTimerRef.current) {
			clearTimeout(searchDebounceTimerRef.current)
		}

		const trimmed = String(searchValue || '').trim()

		if (trimmed.length < 3) {
			setSearchResultsOpen(false)
			setSearchResults([])
			setSearchFeedback('')
			return
		}

		searchDebounceTimerRef.current = setTimeout(() => {
			executeSearch(trimmed)
		}, 1000)

		return () => {
			if (searchDebounceTimerRef.current) {
				clearTimeout(searchDebounceTimerRef.current)
			}
		}
	}, [searchValue])

	useEffect(() => {
		const normalizedDate = normalizeScoutDate(dateValue)

		if (normalizedDate !== dateValue) {
			setDateValue(normalizedDate)
		}
	}, [dateValue])

	useEffect(() => {
		if (selectedDateOption?.endDate && selectedDateOption.endDate !== dateValue) {
			setDateValue(selectedDateOption.endDate)
		}
	}, [dateValue, selectedDateOption])


	useEffect(() => {
		if (!selectedAccountId) {
			setConversations([])
			setAnalytics(null)
			return
		}

		let isMounted = true
		setLoading(true)
		
		Promise.all([
			getScoutAccountAnalytics({
				accountId: selectedAccountId,
				dateStart: selectedDateRange.startDate,
				dateEnd: selectedDateRange.endDate,
			}),
			listScoutConversations({ accountId: selectedAccountId, sort: 'desc' }),
		])
			.then(([nextAnalytics, nextConversations]) => {
				if (!isMounted) {
					return
				}

				setAnalytics(nextAnalytics)
				setConversations(nextConversations)
				setSelectedConversationId((currentConversationId) => {
					const hasCurrentConversation = nextConversations.some(
						(conversation) => conversation.id === currentConversationId,
					)

					if (currentConversationId && !hasCurrentConversation) {
						return ''
					}

					return currentConversationId
				})
			})
			.catch((nextError) => {
				
			})
			.finally(() => {
				if (isMounted) {
					setLoading(false)
				}
			})

		return () => {
			isMounted = false
		}
	}, [selectedAccountId, selectedDateRange.endDate, selectedDateRange.startDate])

	const selectedAccount = useMemo(() => {
		return accounts.find((account) => String(account.id) === String(selectedAccountId)) || null
	}, [accounts, selectedAccountId])

	const selectedAccountAvatarClassName = useMemo(() => {
		return isScoutAccountActive(selectedAccount)
			? 'scout-account-card__avatar scout-account-card__avatar--online'
			: 'scout-account-card__avatar'
	}, [selectedAccount])

	const applyRealtimeConversationEvent = useCallback((eventName, payload) => {
		const items = getSocketDataItems(payload)
		if (!items.length) {
			return
		}

		setConversations((currentConversations) => {
			let nextConversations = currentConversations

			items.forEach((item) => {
				const existingConversation = nextConversations.find(
					(conversation) => String(conversation?.id || '') === String(item?.remoteJid || item?.id || ''),
				)
				const nextConversation = eventName === 'messages.upsert'
					? buildConversationFromRealtimeMessage({
						message: item,
						existingConversation,
						selectedConversationId: selectedConversationIdRef.current,
					})
					: buildConversationFromRealtimeChat({
						chat: item,
						existingConversation,
					})

				// console.log('SCOUT LOG CONVERSATION RESOLUTION DEBUG', JSON.stringify({
				// 	eventName,
				// 	incomingRemoteJid: item?.key?.remoteJid || item?.remoteJid || item?.id,
				// 	incomingRemoteJidAlt: item?.key?.remoteJidAlt || item?.remoteJidAlt,
				// 	resolvedId: nextConversation?.id,
				// 	existingConversationId: existingConversation?.id,
				// 	currentConversationIds: nextConversations.map((conversation) => conversation.id),
				// }, null, 2))

				if (nextConversation) {
					nextConversations = upsertConversation(nextConversations, nextConversation)
				}
			})

			return nextConversations
		})
	}, [])

	const applyRealtimeFocusedConversationEvent = useCallback((payload) => {
		const items = getSocketDataItems(payload)
		if (!items.length) {
			return
		}

		items.forEach((item) => {
			const remoteJid = getRealtimeMessageRemoteJid(item)
			if (!remoteJid || remoteJid !== selectedConversationIdRef.current) {
				return
			}

			if (getRealtimeMessageType(item) === 'pollUpdateMessage') {
				setMessages((currentMessages) => (
					applyRealtimePollUpdateToMessages(currentMessages, item)
				))
				return
			}

			const normalizedMessage = normalizeRealtimeMessage(item)
			if (!normalizedMessage) {
				return
			}

			setMessages((currentMessages) => {
				const currentRenderableIds = new Set(
					currentMessages.filter(isRenderableMessage).map((message) => getMessageIdentifier(message)),
				)
				const nextMessages = mergeMessages(currentMessages, [normalizedMessage])
				const isNewRenderableMessage = isRenderableMessage(normalizedMessage)
					&& !currentRenderableIds.has(getMessageIdentifier(normalizedMessage))

				if (isNewRenderableMessage) {
					if (threadNearBottomRef.current) {
						window.requestAnimationFrame(() => {
							if (threadRef.current) {
								threadRef.current.scrollTop = threadRef.current.scrollHeight
							}
						})
						setPendingNewMessagesCount(0)
					} else {
						setPendingNewMessagesCount((current) => current + 1)
					}
				}

				return nextMessages
			})
		})
	}, [])

	const handleSocketEvent = useCallback((eventName, payload) => {
		// console.log('SCOUT LOG SOCKET DEBUG', JSON.stringify({
		// 	eventName,
		// 	instance: payload?.instance,
		// 	items: getSocketDataItems(payload).map((item) => ({
		// 		messageId: item?.key?.id || item?.id,
		// 		messageType: item?.messageType,
		// 		remoteJid: item?.remoteJid,
		// 		remoteJidAlt: item?.remoteJidAlt,
		// 		keyRemoteJid: item?.key?.remoteJid,
		// 		keyRemoteJidAlt: item?.key?.remoteJidAlt,
		// 		source: item?.source,
		// 		pushName: item?.pushName,
		// 		fromMe: item?.key?.fromMe,
		// 	})),
		// }, null, 2))

		if (payload?.instance && payload.instance !== selectedAccount?.instance_name) {
			return
		}

		applyRealtimeConversationEvent(eventName, payload)

		if (eventName === 'messages.upsert') {
			applyRealtimeFocusedConversationEvent(payload)
		}
	}, [applyRealtimeConversationEvent, applyRealtimeFocusedConversationEvent, selectedAccount?.instance_name])

	useEffect(() => {
		if (!SCOUT_WEBSOCKET_ENABLED) {
			return undefined
		}

		const { urls: websocketUrls, path: websocketPath, skippedInsecureUrls } = getSocketConnectionConfig()
		const wsApiKey = process.env.REACT_APP_DEFAULT_EVO_API_TOKEN

		if (skippedInsecureUrls.length) {
			console.warn('Scout websocket skipped insecure URL(s) on HTTPS page', skippedInsecureUrls)
		}

		if (!selectedAccountId || !selectedAccount?.instance_name || !wsApiKey || !websocketUrls.length) {
			return undefined
		}

		const [websocketUrl] = websocketUrls

		const socketOptions = {
			transports: SOCKET_TRANSPORTS,
			path: websocketPath,
			query: { apikey: wsApiKey },
			auth: { apikey: wsApiKey },
			upgrade: false,
			reconnection: true,
			reconnectionAttempts: 2,
			reconnectionDelay: 1000,
			timeout: 20000,
		}

		const setupSources = (socket, sourceName) => {
			const onConnect = () => {}
			const onDisconnect = () => {}
			const onConnectError = (error) => {
				console.error(`Scout websocket connection error (${sourceName})`, {
					error,
					url: websocketUrl,
					path: websocketPath,
				})
			}
			const socketListeners = new Map()

			socket.on('connect', onConnect)
			socket.on('disconnect', onDisconnect)
			socket.on('connect_error', onConnectError)

			SOCKET_EVENTS.forEach((event) => {
				const listener = (payload) => handleSocketEvent(event, payload)
				socketListeners.set(event, listener)
				socket.on(event, listener)
			})

			return () => {
				socket.off('connect', onConnect)
				socket.off('disconnect', onDisconnect)
				socket.off('connect_error', onConnectError)
				SOCKET_EVENTS.forEach((event) => socket.off(event, socketListeners.get(event)))
			}
		}

		const rootSocket = io(websocketUrl, socketOptions)
		socketRef.current = rootSocket
		const socketCleanup = setupSources(rootSocket, 'root')

		return () => {
			socketCleanup()
			if (socketRef.current) {
				socketRef.current.disconnect()
				socketRef.current = null
			}
		}
	}, [selectedAccountId, selectedAccount?.instance_name, handleSocketEvent, selectedAccount])

	const scrollThreadToBottom = useCallback(() => {
		window.requestAnimationFrame(() => {
			if (threadRef.current) {
				threadRef.current.scrollTop = threadRef.current.scrollHeight
			}
		})
	}, [])

	const loadConversationMessages = useCallback(async ({ reset = false, cursor = 0, chatMessageId } = {}) => {
		if (!selectedAccountId || !selectedConversationId || (!reset && messagesLoadingRef.current)) {
			return
		}

		const requestVersion = conversationViewVersionRef.current
		const controller = new AbortController()
		const selectedAccountPhone = selectedAccount?.phone_number

		abortManagedRequest(activeThreadRequestRef)
		activeThreadRequestRef.current = { controller, version: requestVersion }

		const ownsRequest = () => {
			return activeThreadRequestRef.current.controller === controller
				&& activeThreadRequestRef.current.version === requestVersion
		}

		const isCurrentRequest = () => {
			return ownsRequest()
				&& conversationViewVersionRef.current === requestVersion
				&& !controller.signal.aborted
		}

		const previousScrollHeight = threadRef.current?.scrollHeight || 0
		const previousScrollTop = threadRef.current?.scrollTop || 0

		messagesLoadingRef.current = true
		setInitialMessagesLoading(reset)
		setMessagesLoading(true)
		
		try {
			const pendingMessages = listScoutMessages({
				accountId: selectedAccountId,
				conversationId: selectedConversationId,
				limit: DEFAULT_MESSAGE_LIMIT,
				skip: cursor,
				sort: '-messageTimestamp',
				slimResponse: 1,
				chatMessageId,
				signal: controller.signal,
			})

			const pendingAnalytics = reset
				? getScoutConversationAnalytics({
					accountId: selectedAccountId,
					conversationId: selectedConversationId,
					date: dateValue,
					signal: controller.signal,
				})
				: Promise.resolve(null)

			const [nextBatch, nextConversationAnalytics] = await Promise.all([pendingMessages, pendingAnalytics])

			if (!isCurrentRequest()) {
				return
			}

			let resolvedBatch = filterThreadMessages(Array.isArray(nextBatch) ? nextBatch : [])

			if (reset && !resolvedBatch.length && selectedAccountPhone) {
				resolvedBatch = await getScoutConversationExport({
					accountPhone: selectedAccountPhone,
					date: dateValue,
					conversationId: selectedConversationId,
					signal: controller.signal,
				})

				if (!isCurrentRequest()) {
					return
				}

				resolvedBatch = filterThreadMessages(resolvedBatch)
			}

			if (reset) {
				setMessages((currentMessages) => mergeMessages(resolvedBatch, currentMessages))
				setConversationAnalytics(nextConversationAnalytics)
				setMessageCursor(resolvedBatch.length ? DEFAULT_MESSAGE_LIMIT : -1)

				scrollThreadToBottom()
			} else {
				if (threadRef.current && resolvedBatch.length && isCurrentRequest()) {
					pendingHistoryScrollAdjustmentRef.current = {
						previousScrollHeight,
						previousScrollTop,
						version: requestVersion,
					}
				}

				setMessages((currentMessages) => mergeMessages(resolvedBatch, currentMessages))
				setMessageCursor(resolvedBatch.length ? cursor + DEFAULT_MESSAGE_LIMIT : -1)
			}
		} catch (nextError) {
			if (isAbortError(nextError) || !isCurrentRequest()) {
				return
			}
			
		} finally {
			if (conversationViewVersionRef.current === requestVersion && !controller.signal.aborted) {
				messagesLoadingRef.current = false
				setInitialMessagesLoading(false)
				setMessagesLoading(false)
			}

			if (ownsRequest()) {
				activeThreadRequestRef.current.controller = null
			}
		}
	}, [abortManagedRequest, dateValue, scrollThreadToBottom, selectedAccount, selectedAccountId, selectedConversationId])

	useEffect(() => {
		setContact(null)
		setRightSidebar(window.innerWidth >= 600 ? "insights" : null)
		
		if (!selectedAccountId || !selectedConversationId) {
			setMessages([])
			setMessageCursor(0)
			setConversationAnalytics(null)
			return
		}

		setMessages([])
		setMessageCursor(0)
		setConversationAnalytics(null)
		loadConversationMessages({ reset: true, cursor: 0, chatMessageId: anchorMessageId || undefined })

		return () => {
			abortManagedRequest(activeThreadRequestRef)
		}
	}, [abortManagedRequest, anchorMessageId, dateValue, loadConversationMessages, selectedAccountId, selectedConversationId])

	useEffect(() => {
		setPendingNewMessagesCount(0)
		setThreadNearBottom(true)
	}, [selectedConversationId])

	useEffect(() => {
		const nextParams = {}

		if (selectedAccountId) {
			nextParams.accountId = selectedAccountId
		}

		if (selectedConversationId) {
			nextParams.conversationId = selectedConversationId
		}

		if (dateValue) {
			nextParams.date = dateValue
		}

		if (selectedDatePreset) {
			nextParams.period = selectedDatePreset
		}

		if (anchorMessageId) {
			nextParams.anchorMessageId = anchorMessageId
		}

		setSearchParams(nextParams, { replace: true })
	}, [anchorMessageId, dateValue, selectedAccountId, selectedConversationId, selectedDatePreset, setSearchParams])

	const filteredConversations = useMemo(() => {
		return getVisibleConversations(conversations)
	}, [conversations])

	const accountByInstanceName = useMemo(() => {
		return accounts.reduce((lookup, account) => {
			if (account?.instance_name) {
				lookup[account.instance_name] = account
			}

			return lookup
		}, {})
	}, [accounts])

	const normalizedSearchResults = useMemo(() => {
		return searchResults.map((entry, index) => {
			const account = accountByInstanceName[entry?.owner] || null
			const conversationId = entry?.info?.id || ''

			return {
				id: `${conversationId || entry?.owner || 'search'}-${index}`,
				conversationId,
				conversationName: entry?.conversationName || 'Conversa monitorada',
				account,
				messages: Array.isArray(entry?.messages) ? entry.messages : [],
			}
		})
	}, [accountByInstanceName, searchResults])

	const selectedConversation = useMemo(() => {
		return filteredConversations.find((conversation) => conversation.id === selectedConversationId)
			|| conversations.find((conversation) => conversation.id === selectedConversationId)
			|| null
	}, [conversations, filteredConversations, selectedConversationId])

	const handleOpenSearchConversation = (result, message = null) => {
		if (!result?.conversationId) {
			showSnackbar({ message: 'Não foi possível abrir a conversa a partir deste resultado.', severity: 'warning' })
			return
		}

		const anchorId = message?.id || message?.chatMessageId || message?.messageId
		selectConversation(String(result.conversationId), {
			accountId: result?.account?.id,
			anchorId,
		})

		setSearchResultsOpen(false)
	}

	const handleLoadMediaMessage = useCallback(async (message) => {
		const messageId = getMessageIdentifier(message)

		if (!selectedAccountId || !selectedConversationId || !messageId || loadingMediaIds[messageId]) {
			return
		}

		setLoadingMediaIds((currentState) => ({
			...currentState,
			[messageId]: true,
		}))

		try {
			const fullMessage = await getScoutMessageById({
				accountId: selectedAccountId,
				conversationId: selectedConversationId,
				messageId,
			})

			if (!fullMessage) {
				throw new Error('Não foi possível carregar esta mídia.')
			}

			setMessages((currentMessages) => currentMessages.map((entry, index) => {
				return getMessageIdentifier(entry, index) === messageId
					? { ...entry, ...fullMessage }
					: entry
			}))
		} catch (nextError) {
			showSnackbar({
				message: nextError.message || 'Não foi possível carregar a mídia.',
				severity: 'error',
			})
		} finally {
			setLoadingMediaIds((currentState) => {
				const nextState = { ...currentState }
				delete nextState[messageId]
				return nextState
			})
		}
	}, [loadingMediaIds, selectedAccountId, selectedConversationId, showSnackbar])


	useEffect(() => {
		if (!anchorMessageId || !threadRef.current) {
			return
		}

		const targetMessage = threadRef.current.querySelector(`[data-message-id="${anchorMessageId}"]`)

		if (targetMessage) {
			targetMessage.scrollIntoView({ behavior: 'smooth', block: 'center' })
			setAnchorMessageId('')
			return
		}

		threadRef.current.scrollTop = threadRef.current.scrollHeight
		setAnchorMessageId('')
	}, [anchorMessageId, messages])

	useEffect(() => {
		setLoadingMediaIds({})
	}, [selectedConversationId])

	const totalMessages = (analytics?.conversation_messages || 0) + (analytics?.group_messages || 0)

	const handleBackNavigation = useCallback(() => {
		if (selectedConversationId) {
			selectConversation('')
			return
		}

		navigate('/home')
	}, [navigate, selectConversation, selectedConversationId])

	const accountSummaries = useMemo(() => {
		if (Array.isArray(analytics?.summaries) && analytics.summaries.length) {
			return analytics.summaries
				.filter((item) => item?.summary)
				.map((item, index) => ({
					id: item.id || `summary-${index}`,
					name: item.name || 'Conversa monitorada',
					summary: String(item.summary).trim(),
				}))
		}

		const summarySource = analytics?.summary || analytics?.resumo || analytics?.resumos

		if (Array.isArray(summarySource)) {
			return summarySource
				.map((line, index) => ({
					id: `legacy-summary-${index}`,
					name: 'Resumo',
					summary: String(line).trim(),
				}))
				.filter((item) => item.summary)
		}

		if (typeof summarySource === 'string') {
			return summarySource
				.split(/\n+/)
				.map((line, index) => ({
					id: `legacy-summary-${index}`,
					name: 'Resumo',
					summary: line.trim(),
				}))
				.filter((item) => item.summary)
		}

		return []
	}, [analytics])

	const accountOverviewGraphics = useMemo(() => {
		const viewModel = createHomeGraphicsViewModel({
			analytics,
			selectedAccount,
			useMockData: false,
		})

		if (viewModel.summaries.length) {
			return viewModel
		}

		return {
			...viewModel,
			summaries: accountSummaries.map((item) => ({
				...item,
				tone: 'neutral',
			})),
		}
	}, [accountSummaries, analytics, selectedAccount])

	const insightLines = useMemo(() => {
		const summary = conversationAnalytics?.summary || ''

		if (!summary) {
			return []
		}

		return summary
			.split(/\n+/)
			.map((line) => line.trim())
			.filter(Boolean)
	}, [conversationAnalytics?.summary])

	const renderedMessages = useMemo(() => {
		return messages.filter(isRenderableMessage)
	}, [messages])

	const messageReactionLookup = useMemo(() => {
		return buildMessageReactionLookup(messages)
	}, [messages])

	const [editHistoryEntries, setEditHistoryEntries] = useState([])
	const editHistoryHasDeletion = useMemo(() => {
		return editHistoryEntries.some((entry) => entry?.type === 'DELETED')
	}, [editHistoryEntries])

	const handleOpenEditHistory = useCallback((message) => {
		const history = getEditHistory(message, messages)
		if (history.length) {
			setEditHistoryEntries([message, ...history])
		}
	}, [messages])

	const handleCloseEditHistory = useCallback(() => {
		setEditHistoryEntries([])
	}, [])

	const chatOpen = Boolean(selectedConversationId)
	const exportEmailValid = validateEmail(exportEmail)
	const canExport = Boolean(selectedAccount && selectedConversation && exportEmailValid && exportDateStart && exportDateEnd)
	const overviewMainStyle = !chatOpen
		? {
			padding: '1rem',
			display: 'flex',
			flexDirection: 'column',
			flex: '1 1 auto',
			minHeight: 0,
			overflow: 'auto',
		  }
		: { padding: 0 }
	const overviewGridStyle = !chatOpen
		? {
			flex: '1 1 auto',
			minHeight: 0,
			marginTop: 0,
			alignItems: 'stretch',
		  }
		: undefined
	const overviewPanelStyle = !chatOpen ? { height: '100%', minHeight: 0 } : undefined
	const overviewBodyStyle = !chatOpen ? { minHeight: 0, overflow: 'hidden' } : undefined
	const overviewSummaryListStyle = !chatOpen ? { flex: '1 1 auto', minHeight: 0, overflow: 'hidden' } : undefined

	const handleThreadScroll = useCallback(() => {
		if (!threadRef.current || messagesLoading || messageCursor === -1 || suppressThreadScrollHandlerRef.current) {
			return
		}

		const scrollTop = threadRef.current.scrollTop
		const clientHeight = threadRef.current.clientHeight
		const scrollHeight = threadRef.current.scrollHeight
		const distanceFromBottom = scrollHeight - (scrollTop + clientHeight)
		const nearBottom = distanceFromBottom <= 120

		setThreadNearBottom(nearBottom)
		if (nearBottom && pendingNewMessagesCount > 0) {
			setPendingNewMessagesCount(0)
		}

		if (scrollTop <= TOP_LOAD_THRESHOLD) {
			loadConversationMessages({ reset: false, cursor: messageCursor })
		}
	}, [loadConversationMessages, messageCursor, messagesLoading, pendingNewMessagesCount])

	const handleExportConversation = useCallback(async () => {
		if (!canExport || !selectedAccount || !selectedConversation) {
			return
		}

		setExportLoading(true)
		setExportFeedback('')

		try {
			const stableTitle = getStableConversationTitle(selectedConversation)
			const chatName = stableTitle !== formatPhoneNumber(selectedConversation?.phone_num) ? `(${stableTitle})` : ''
			await exportScoutConversationMessages({
				accountId: selectedAccount.id,
				conversationId: selectedConversation.id,
				email: exportEmail.trim(),
				dateStart: exportDateStart,
				dateEnd: exportDateEnd,
				format: exportType,
				numberText: selectedConversation.type === 'GROUP'
					? `para o grupo ${stableTitle}`
					: `para o número ${selectedConversation.phone_num} ${chatName}`,
			})

			setExportFeedback('Exportação iniciada. O histórico sera enviado por e-mail em breve.')
			setExportDialogOpen(false)
		} catch (nextError) {
			setExportFeedback(nextError.message)
		} finally {
			setExportLoading(false)
		}
	}, [canExport, exportDateEnd, exportDateStart, exportEmail, exportType, selectedAccount, selectedConversation, getStableConversationTitle])

	useEffect(() => {
		const loadContact = async () => {
			try {
				let contact = await getContactData(enterprise?.id, selectedConversation?.phone_num)
				if (!contact)
					contact = await createContact(enterprise?.id, selectedConversation)
				setContact(contact)
			} catch (err) {
				emitSnackbar({ message: "Algo deu errado, tente novamente.", severity: "error" })
				setRightSidebar(null)
			}
		}
		if (rightSidebar === "profile" && !contact && renderCRMFeatures(enterprise))
			loadContact()
	}, [rightSidebar, contact, enterprise, selectedConversation])

	return (
		<div className="scout-page scout-page--conversations">
			{exportDialogOpen ? (
				<DefaultModal
					handleClose={() => setExportDialogOpen(false)}
					title="Exportar conversa"
					actionAlign="right"
					subtitle="A conversa será enviada por e-mail e ficará disponível temporariamente, como no Scout do Kwik."
					action={(
					<>
						<Button onClick={() => setExportDialogOpen(false)}>Cancelar</Button>
						<Button variant="contained" onClick={handleExportConversation} disabled={!canExport || exportLoading}>
							{exportLoading ? 'Enviando...' : 'Enviar'}
						</Button>
					</>
				)}
				maxWidth="sm"
			>
				<div className="scout-export-dialog__grid">
					<TextField
						label="E-mail"
						type="email"
						fullWidth
						value={exportEmail}
						onChange={(event) => setExportEmail(event.target.value)}
						error={Boolean(exportEmail) && !exportEmailValid}
						helperText={Boolean(exportEmail) && !exportEmailValid ? 'Informe um e-mail válido.' : ' '}
					/>
					<TextField
						label="Início"
						type="datetime-local"
						fullWidth
						InputLabelProps={{ shrink: true }}
						value={exportDateStart}
						onChange={(event) => setExportDateStart(event.target.value)}
					/>
					<TextField
						label="Fim"
						type="datetime-local"
						fullWidth
						InputLabelProps={{ shrink: true }}
						value={exportDateEnd}
						onChange={(event) => setExportDateEnd(event.target.value)}
					/>
					<div className="scout-export-format-buttons" style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
						<Button
							variant={exportType === 'pdf' ? 'contained' : 'outlined'}
							onClick={() => setExportType('pdf')}
						>
							PDF
						</Button>
						<Button
							variant={exportType === 'csv' ? 'contained' : 'outlined'}
							onClick={() => setExportType('csv')}
						>
							CSV
						</Button>
					</div>
				</div>
			</DefaultModal>
		) : null}

		{Boolean(editHistoryEntries.length) ? (
			<DefaultModal
				open={Boolean(editHistoryEntries.length)}
				handleClose={handleCloseEditHistory}
				title={editHistoryHasDeletion ? 'Mensagem apagada' : 'Histórico de edição'}
				subtitle={editHistoryHasDeletion ? 'Veja a mensagem original e o registro da exclusão' : 'Veja todas as versões desta mensagem'}
				maxWidth="sm"
			>
				<div className="scout-edit-history">
					{editHistoryEntries.map((entry, index) => (
						<div key={`${entry.id || index}-${index}`} className="scout-edit-history__entry">
							<div className="scout-edit-history__meta">
								<span>{index === 0 ? 'Original' : entry.type === 'DELETED' ? 'Apagada em' : entry.type === 'EDITED' ? 'Editado em' : 'Versão'}</span>
								<small>{entry.iso_time || formatTimestampLabel(entry.timestamp)}</small>
							</div>
							<p><LinkifiedText text={formatKwikMessageContent(entry)} /></p>
						</div>
					))}
				</div>
			</DefaultModal>
		) : null}

		{searchResultsOpen ? (
	<SearchResultsModal
		open={searchResultsOpen}
		searchValue={searchValue}
		searchResultsLoading={searchResultsLoading}
		searchFeedback={searchFeedback}
		searchResults={normalizedSearchResults}
		onClose={handleCloseSearchResults}
		onOpenSearchConversation={handleOpenSearchConversation}
	/>
) : null}

		<section className="scout-hero-bar">
				<div>
					<h1 className="scout-hero-bar__title">Olá {user?.first_name || user?.username}, {getGreetings()}!</h1>
					<p className="scout-hero-bar__subtitle">
						{selectedConversation
							? `Visualizando ${getStableConversationTitle(selectedConversation)}`
							: 'Clique em uma conversa para abrir o histórico'}
					</p>
				</div>

				<div className="scout-filter-bar">
					<label className="scout-search-field">
						<SearchIcon />
						<input
							type="search"
							value={searchValue}
							onChange={(event) => setSearchValue(event.target.value)}
							placeholder="Buscar nas conversas"
						/>
					</label>
					<div className="scout-date-dropdown" ref={dateDropdownRef}>
						<button
							type="button"
							className={[
								'scout-date-field',
								'scout-date-field--button',
								isDateMenuOpen ? 'scout-date-field--open' : '',
							].filter(Boolean).join(' ')}
							onClick={() => setIsDateMenuOpen((currentValue) => !currentValue)}
							aria-haspopup="listbox"
							aria-expanded={isDateMenuOpen}
						>
							<span className="scout-date-field__value">{selectedDateLabel}</span>
							<KeyboardArrowDownIcon className={isDateMenuOpen ? 'scout-date-field__chevron scout-date-field__chevron--open' : 'scout-date-field__chevron'} />
						</button>

						{isDateMenuOpen ? (
							<div className="scout-date-dropdown__menu" role="listbox" aria-label="Selecionar período das conversas">
								{baseDateOptions.map((option) => {
									const isActive = option.key === selectedDatePreset

									return (
										<button
											key={option.key || option.value}
											type="button"
											className={isActive ? 'scout-date-dropdown__option scout-date-dropdown__option--active' : 'scout-date-dropdown__option'}
											onClick={() => {
												cancelConversationRequests()
												resetConversationViewState()
												setDateValue(option.endDate || option.value)
												setSelectedDatePreset(option.key || '')
												setIsDateMenuOpen(false)
											}}
											role="option"
											aria-selected={isActive}
										>
											<span>{option.label}</span>
											{isActive ? <span className="scout-date-dropdown__option-indicator" /> : null}
										</button>
									)
								})}
							</div>
						) : null}
					</div>
				</div>
			</section>

			{loading
				? <div className="scout-home-layout">
					<div className="scout-home-loading" aria-live="polite" aria-busy="true">
						<aside className="scout-side-column">
							<section className="scout-panel scout-panel--sidebar scout-loading-panel">
								<div className="scout-panel__heading">
									<span className="scout-loading-chip" />
									<span className="scout-loading-chip" />
								</div>
								<div className="scout-account-list">
									{Array.from({ length: 3 }).map((_, index) => (
										<div key={`account-loading-${index}`} className="scout-account-card scout-loading-block scout-loading-block--account">
											<div className="scout-account-card__avatar scout-loading-orb" />
											<div className="scout-account-card__content">
												<span className="scout-loading-line scout-loading-line--title" />
												<span className="scout-loading-line scout-loading-line--text" />
											</div>
										</div>
									))}
								</div>
							</section>
						</aside>

						<section className="scout-main-column">
							<div className="scout-stats-grid">
								{Array.from({ length: 4 }).map((_, index) => (
									<article key={`stat-loading-${index}`} className="scout-stat-card scout-loading-block">
										<div className="scout-stat-card__header">
											<span className="scout-loading-line scout-loading-line--text scout-loading-line--medium" />
											<span className="scout-loading-dot" />
										</div>
										<div className="scout-stat-card__value-group">
											<span className="scout-loading-line scout-loading-line--value" />
										</div>
									</article>
								))}
							</div>

							<HomeGraphicsSection loading />
						</section>
					</div>
				</div>
				: <div className={chatOpen ? 'scout-conversations-layout scout-conversations-layout--chat-open' : 'scout-conversations-layout'}>
					<ConversationSidebar
						selectedAccount={selectedAccount}
						selectedAccountAvatarClassName={selectedAccountAvatarClassName}
						filteredConversations={filteredConversations}
						selectedConversationId={selectedConversationId}
						selectConversation={selectConversation}
						handleBackNavigation={handleBackNavigation}
						getStableConversationTitle={getStableConversationTitle}
					/>

					<section className="scout-conversations-main" style={overviewMainStyle}>
						{!chatOpen ? (
							<ConversationOverview
								analytics={analytics}
								enterprise={enterprise}
								totalMessages={totalMessages}
								accountOverviewGraphics={accountOverviewGraphics}
								mobileChartOpen={mobileChartOpen}
								onOpenMobileChart={setMobileChartOpen}
								onCloseMobileChart={() => setMobileChartOpen(null)}
								gridStyle={overviewGridStyle}
								panelStyle={overviewPanelStyle}
								bodyStyle={overviewBodyStyle}
								summaryListStyle={overviewSummaryListStyle}
							/>
						) : (
							<div className={!rightSidebar ? 'scout-thread-layout scout-thread-layout--collapsed' : 'scout-thread-layout'}>
								<ConversationThread
									selectedConversation={selectedConversation}
									selectedAccount={selectedAccount}
									getStableConversationTitle={getStableConversationTitle}
									optionsAnchorEl={optionsAnchorEl}
									optionsOpen={optionsOpen}
									onOpenOptions={(event) => setOptionsAnchorEl(event.currentTarget)}
									onCloseOptions={() => setOptionsAnchorEl(null)}
									onBack={() => selectConversation('')}
									onExportConversation={() => {
										if (renderExportConversation(enterprise)) {
											setExportDialogOpen(true)
											setOptionsAnchorEl(null)
										} else {
											showSnackbar({
												message: 'Recurso indisponível no seu plano.',
												severity: 'warning',
											})
										}
									}}
									onShowInsights={() => {
										setRightSidebar('insights')
										setOptionsAnchorEl(null)
									}}
									onShowProfile={() => {
										setRightSidebar('profile')
										setOptionsAnchorEl(null)
									}}
									enterprise={enterprise}
									initialMessagesLoading={initialMessagesLoading}
									threadRef={threadRef}
									onThreadScroll={handleThreadScroll}
									renderedMessages={renderedMessages}
									messagesLoading={messagesLoading}
									loadingMediaIds={loadingMediaIds}
									onLoadMediaMessage={handleLoadMediaMessage}
									messages={messages}
									searchValue={searchValue}
									onOpenEditHistory={handleOpenEditHistory}
									messageReactionLookup={messageReactionLookup}
								/>

								{rightSidebar === 'insights' ? (
									<ConversationInsightsSidebar
										enterprise={enterprise}
										insightLines={insightLines}
										initialMessagesLoading={initialMessagesLoading}
										conversationAnalytics={conversationAnalytics}
										renderedMessages={renderedMessages}
										onClose={() => setRightSidebar(null)}
									/>
								) : null}

								{rightSidebar === 'profile' ? (
									<ConversationProfileSidebar
										enterprise={enterprise}
										selectedConversation={selectedConversation}
										contact={contact}
										getStableConversationTitle={getStableConversationTitle}
										onClose={() => setRightSidebar(null)}
										onSaveName={async (value) => {
											try {
												await updateContact(enterprise?.id, contact?.id, 'name', value)
												setContact((prev) => ({ ...prev, name: value }))
												emitSnackbar({ message: 'Nome atualizado com sucesso.', severity: 'success' })
											} catch (err) {
												emitSnackbar({ message: 'Algo deu errado, tente novamente.', severity: 'error' })
											}
										}}
										onSaveEnterprise={async (id, name) => {
											try {
												await updateContact(enterprise?.id, contact?.id, 'contact_enterprise', id)
												setContact((prev) => ({ ...prev, contact_enterprise: id, enterprise_name: name }))
												emitSnackbar({ message: 'Empresa atualizada com sucesso.', severity: 'success' })
											} catch (err) {
												emitSnackbar({ message: 'Algo deu errado, tente novamente.', severity: 'error' })
											}
										}}
										onSaveEmail={async (value) => {
											try {
												await updateContact(enterprise?.id, contact?.id, 'email', value)
												setContact((prev) => ({ ...prev, email: value }))
												emitSnackbar({ message: 'E-mail atualizado com sucesso.', severity: 'success' })
											} catch (err) {
												emitSnackbar({ message: 'Algo deu errado, tente novamente.', severity: 'error' })
											}
										}}
										onSaveBirthday={async (value) => {
											try {
												await updateContact(enterprise?.id, contact?.id, 'birthday', value)
												setContact((prev) => ({ ...prev, birthday: dayjs(value).toISOString() }))
												emitSnackbar({ message: 'Aniversário atualizado com sucesso.', severity: 'success' })
											} catch (err) {
												emitSnackbar({ message: 'Algo deu errado, tente novamente.', severity: 'error' })
											}
										}}
										onSaveCpf={async (value) => {
											try {
												await updateContact(enterprise?.id, contact?.id, 'cpf', value)
												setContact((prev) => ({ ...prev, cpf: value }))
												emitSnackbar({ message: 'CPF atualizado com sucesso.', severity: 'success' })
											} catch (err) {
												emitSnackbar({ message: 'Algo deu errado, tente novamente.', severity: 'error' })
											}
										}}
										onSaveCustomerCode={async (value) => {
											try {
												await updateContact(enterprise?.id, contact?.id, 'customer_code', value)
												setContact((prev) => ({ ...prev, customer_code: value }))
												emitSnackbar({ message: 'Código atualizado com sucesso.', severity: 'success' })
											} catch (err) {
												emitSnackbar({ message: 'Algo deu errado, tente novamente.', severity: 'error' })
											}
										}}
									/>
								) : null}
							</div>
						)}

						{exportFeedback ? <p className="scout-inline-feedback">{exportFeedback}</p> : null}
						{loading ? <p className="scout-inline-feedback">Carregando dados...</p> : null}
					</section>
				</div>
			}
		</div>
	)
}

export default Conversations
